import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePricing, dkk, smsLine } from "../lib/pricing";
import Testimonials from "../components/Testimonials";
import { MessageSquareReply, Boxes, ScanLine, Zap, Wallet, ShieldCheck, Check, MessageCircle, CalendarClock, BarChart3, ChevronDown, CheckCircle2, BellRing } from "lucide-react";

function SiteShell({ children, wide = true }) {
  const year = new Date().getFullYear();
  const { store, logout } = useAuth();
  return (
    <>
      <header className="site-head">
        <div className="in">
          <Link className="brand on-light" to="/" aria-label="JaTak Pro">
            <img className="brand-ico" src="/logo.svg?v=2" alt="" width="30" height="30" /><span className="brand-name">JaTak Pro</span>
          </Link>
          <nav>
            <Link to="/funktioner" data-testid="nav-features-link">Funktioner</Link>
            <Link to="/priser" data-testid="nav-pricing-link">Priser</Link>
            {store ? (
              <>
                <button type="button" className="foot-link-btn" onClick={logout} data-testid="landing-logout">
                  Log ud
                </button>
                <Link className="btn jatak small" to="/overblik" data-testid="goto-overview-link">
                  {store.is_owner ? "Gå til admin" : "Gå til Overblik"}
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="login-link">
                  Log ind
                </Link>
                <Link className="btn jatak small" to="/signup" data-testid="signup-link">
                  Prøv gratis
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>
      <main className={wide ? "site" : "auth"}>{children}</main>
      <footer className="site-foot">
        <div className="foot-top">
          <div className="foot-brand">
            <Link className="brand" to="/">
              <img className="brand-ico" src="/logo-mark.svg?v=2" alt="" width="30" height="30" />JaTak Pro
            </Link>
            <p>
              Ja tak-robot til Facebook. Robotten svarer kunderne, styrer lager og venteliste – og sørger for at varerne
              faktisk bliver hentet.
            </p>
            <div className="foot-badges">
              <span>
                <Zap size={15} /> Svar på sekunder
              </span>
              <span>
                <Wallet size={15} /> 0 kr. pr. salg
              </span>
              <span>
                <ShieldCheck size={15} /> Ingen binding
              </span>
            </div>
          </div>
          <div className="foot-col">
            <h4>Produkt</h4>
            <Link to="/#saadan">Sådan virker det</Link>
            <Link to="/funktioner" data-testid="footer-features-link">Funktioner</Link>
            <Link to="/priser" data-testid="footer-pricing-link">Priser</Link>
            <Link to="/#faq">FAQ</Link>
            <Link to="/signup">Prøv gratis</Link>
          </div>
          <div className="foot-col">
            <h4>Kontakt</h4>
            <Link to="/support" data-testid="footer-support">Support</Link>
            <a href="mailto:info@jatakpro.dk" data-testid="footer-email">
              info@jatakpro.dk
            </a>
            <span>Adtimera ApS</span>
            <span>CVR 40551867</span>
          </div>
          <div className="foot-col">
            <h4>Juridisk</h4>
            <Link to="/privatliv" data-testid="footer-privacy">Privatliv</Link>
            <Link to="/vilkaar" data-testid="footer-terms">Vilkår</Link>
            <a href="/guide/" data-testid="footer-guide">Guide</a>
            <button
              type="button"
              className="foot-link-btn"
              onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              data-testid="manage-cookies-btn"
            >
              Administrer cookies
            </button>
          </div>
        </div>
        <div className="foot-bottom">
          <div className="foot-bottom-in">
            <span>© {year} JaTak Pro · Adtimera ApS</span>
            <span>Lavet i Danmark 🇩🇰</span>
          </div>
        </div>
      </footer>
    </>
  );
}

export { SiteShell };

export function faqsFor(p, ctx = "landing") {
  const price = `${dkk(p.price_monthly_dkk)} kr. pr. måned ekskl. moms`;
  const common = [
    { q: "Hvad koster det?", a: `${price} – alt er inkluderet. Ubegrænset antal tilbud og ordrer, ${p.sms_included} SMS pr. butik pr. måned og dansk support. Ingen skjulte gebyrer.` },
    { q: "Tager I noget fra omsætningen?", a: "Nej. I betaler kun den faste månedspris – 0 kr. i gebyr pr. salg. Uanset om I sælger 10 eller 10.000 varer, er prisen den samme." },
    { q: "Kan jeg opsige, når jeg har lyst?", a: `Ja. Der er ingen binding – skriv til ${p.contact_email}, så stopper vi abonnementet ved udgangen af den betalte måned.` },
  ];
  if (ctx === "pricing") {
    return [
      ...common,
      { q: "Er prisen med eller uden moms?", a: `Prisen er ${price}. Med ${p.vat_pct} % moms bliver det ${dkk(p.price_monthly_incl_vat)} kr. pr. måned.` },
      { q: "Hvad med SMS?", a: `${smsLine(p)} Bliver de inkluderede SMS brugt op, og har du ikke slået ekstra SMS til, stopper SMS'erne resten af måneden – kunderne får stadig besked på Messenger.` },
      { q: "Hvad sker der efter prøveperioden?", a: `Prøveperioden varer ${p.trial_days} dage. Starter I ikke abonnementet, låses adgangen til JaTak Pro, indtil fakturaen er betalt. Robotten svarer stadig kunderne på igangværende tilbud imens.` },
      { q: "Kan jeg prøve uden Facebook?", a: "Ja. I kan teste hele systemet i testtilstand med det samme – helt uden at forbinde jeres Facebook-side." },
      { q: "Hvordan betaler jeg?", a: `Via faktura og bankoverførsel. I får en faktura på mail hver måned (hent den som PDF) med ${p.due_days} dages betalingsfrist – intet kort og intet automatisk træk.` },
      { q: "Er der fri support?", a: "Ja. Dansk support er inkluderet i prisen. Skriv til os, så hjælper vi jer i gang og svarer på jeres spørgsmål undervejs." },
      { q: "Har du flere butikker?", a: `Så får I én samlet faktura med ${dkk(p.price_monthly_dkk)} kr./md. ekskl. moms pr. butik. Tilføj butikkerne under dit login, og skift mellem dem i menuen – Facebook, tilbud, kunder og statistik er stadig pr. butik. Prøveperioden gælder for den første butik.` },
    ];
  }
  return [
    common[0],
    { q: "Hvordan kommer jeg i gang?", a: "Opret jer med jeres mail, forbind jeres Facebook-side med ét klik, og slå jeres første tilbud op. Robotten klarer resten – I kan prøve det hele i testtilstand uden Facebook først." },
    common[1],
    { q: "Har jeg nok følgere på Facebook?", a: "Ja – JaTak Pro virker uanset sidens størrelse. Mange butikker sælger fint til nogle få hundrede lokale følgere, fordi ja tak-tilbud deles og kommenteres flittigt." },
    common[2],
    { q: "Er der fri support?", a: "Ja. Dansk support er inkluderet i prisen. Skriv til os, så hjælper vi jer i gang og svarer på jeres spørgsmål undervejs." },
    { q: "Er det hurtigt at komme i gang?", a: "Det tager under et minut at oprette en konto, og I kan teste hele systemet i testtilstand med det samme – helt uden at forbinde Facebook." },
    { q: "Skal mine kunder installere noget?", a: "Nej. Jeres kunder skriver bare \"ja tak\" i kommentarerne som de plejer. Robotten svarer dem og sender afhentningskoden i en privat Messenger-besked – helt automatisk." },
  ];
}

export function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={"faq-item" + (open ? " open" : "")}>
      <button className="faq-q" onClick={() => setOpen((o) => !o)} data-testid="faq-question" aria-expanded={open}>
        <span>{q}</span>
        <ChevronDown size={20} className="faq-chev" />
      </button>
      {open && (
        <div className="faq-a" data-testid="faq-answer">
          {a}
        </div>
      )}
    </div>
  );
}

const fmtCount = (n) => (n > 1000 ? `${Math.floor(n / 100) * 100}+` : n > 10 ? `${Math.floor(n / 10) * 10}+` : String(n));

function useStoreCount() {
  const [n, setN] = useState(null);
  useEffect(() => {
    api.get("/public/stats").then((r) => setN(r.data?.stores_count ?? null)).catch(() => {});
  }, []);
  return n;
}

export default function Landing() {
  const pricing = usePricing();
  const trial = pricing.trial_days;
  const stores = useStoreCount();
  return (
    <SiteShell>
      <section className="hero">
        <div className="hero-copy">
          <span className="eyebrow" data-testid="hero-eyebrow">
            <span className="dotpulse" />
            Ja tak-robot til Facebook
          </span>
          <h1>Ja tak-tilbud på Facebook, der passer sig selv</h1>
          <p className="lead">
            Robotten svarer hver kunde med det samme, holder styr på lageret og ventelisten – og sørger for at varerne
            faktisk bliver hentet.
          </p>
          <div className="actions">
            <Link className="btn jatak big" to="/signup" data-testid="hero-signup-btn">
              Prøv gratis i {trial} dage
            </Link>
            <a className="btn big" href="#saadan" data-testid="hero-how-btn">
              Se hvordan det virker
            </a>
          </div>
          <p className="muted" style={{ marginTop: 16, fontSize: 13 }}>
            14 dages gratis prøve · intet betalingskort · ingen binding
          </p>
          <div className="hero-trust">
            <span>
              <Zap size={16} /> Svar på sekunder
            </span>
            <span>
              <Wallet size={16} /> 0 kr. pr. salg
            </span>
            <span>
              <ShieldCheck size={16} /> Ingen binding
            </span>
          </div>
        </div>
        <div className="hero-demo" aria-hidden="true">
          <div className="fb-post">
            <header>
              <span className="fb-avatar">KB</span>
              <div>
                <b>Købmand Berg</b>
                <br />
                <small className="muted">for 3 min.</small>
              </div>
            </header>
            <div className="fb-text">
              {`HEL FLÆSKESTEG, CA. 2 KG\n💰 49,95 kr. (normalpris 89,95 kr.)\n👉 Skriv "ja tak" + antal`}
            </div>
            <div className="fb-comments">
              <div className="fb-c">
                <span className="fb-avatar user">AJ</span>
                <div>
                  <div className="fb-bubble">
                    <b>Anna Jensen</b>Ja tak 2 🙏
                  </div>
                </div>
              </div>
              <div className="fb-c reply">
                <span className="fb-avatar">KB</span>
                <div>
                  <div className="fb-bubble">
                    <b>Købmand Berg</b>✅ Anna, du har 2 stk. reserveret! Hent søndag kl. 14–18. Din afhentningskode er
                    sendt til dig i en privat besked 📩
                  </div>
                </div>
              </div>
              <div className="fb-c">
                <span className="fb-avatar user">MN</span>
                <div>
                  <div className="fb-bubble">
                    <b>Mads Nielsen</b>jatak
                  </div>
                </div>
              </div>
              <div className="fb-c reply">
                <span className="fb-avatar">KB</span>
                <div>
                  <div className="fb-bubble">
                    <b>Købmand Berg</b>⏳ Udsolgt lige nu, Mads – du er nr. 1 på ventelisten.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`band${stores != null ? " five" : ""}`} data-testid="hero-stats">
        <div className="stat" data-testid="hero-stat-reply">
          <b className="stat-v"><span className="stat-n">&lt; 1</span><span className="stat-u">sek.</span></b>
          <span className="stat-l">svartid til kunden</span>
        </div>
        <div className="stat" data-testid="hero-stat-always">
          <b className="stat-v"><span className="stat-n">24/7</span></b>
          <span className="stat-l">robotten sover aldrig</span>
        </div>
        <div className="stat" data-testid="hero-stat-fee">
          <b className="stat-v"><span className="stat-n">0</span><span className="stat-u">kr.</span></b>
          <span className="stat-l">i gebyr pr. salg</span>
        </div>
        <div className="stat" data-testid="hero-stat-trial">
          <b className="stat-v"><span className="stat-n">{trial}</span><span className="stat-u">dage</span></b>
          <span className="stat-l">gratis prøve</span>
        </div>
        {stores != null && (
          <div className="stat" data-testid="hero-stat-stores">
            <b className="stat-v"><span className="stat-n" data-testid="hero-stat-stores-value">{fmtCount(stores)}</span><span className="stat-u">butikker</span></b>
            <span className="stat-l">bruger allerede JaTak Pro</span>
          </div>
        )}
      </section>

      <section className="howto" id="saadan">
        <div className="sec-head">
          <span className="eyebrow">Sådan virker det</span>
          <h2>Fra "ja tak" til varen i hånden</h2>
          <p>Fire trin, der kører helt af sig selv – I rører ikke en finger, før varen skal pakkes.</p>
        </div>
        <div className="flow" data-testid="howto-flow">
          <div className="flow-step">
            <span className="flow-num">1</span>
            <div className="flow-ico"><MessageSquareReply size={22} /></div>
            <h3>Kunden skriver "ja tak"</h3>
            <p>En følger kommenterer fx "ja tak 2" på jeres Facebook-opslag.</p>
          </div>
          <div className="flow-step">
            <span className="flow-num">2</span>
            <div className="flow-ico"><CheckCircle2 size={22} /></div>
            <h3>Robotten reserverer og svarer</h3>
            <p>Varen reserveres med det samme. Robotten svarer kunden offentligt og synes godt om kommentaren.</p>
          </div>
          <div className="flow-step">
            <span className="flow-num">3</span>
            <div className="flow-ico"><MessageCircle size={22} /></div>
            <h3>Afhentningskode i Messenger</h3>
            <p>Kunden får en privat besked med sin personlige afhentningskode og alle detaljer om ordren.</p>
          </div>
          <div className="flow-step">
            <span className="flow-num">4</span>
            <div className="flow-ico"><ScanLine size={22} /></div>
            <h3>Kunden henter i butikken</h3>
            <p>I søger koden frem på udleveringsskærmen og trykker "udleveret". Færdig.</p>
          </div>
        </div>
      </section>

      <section className="steps" style={{ borderTop: 0 }}>
        <div className="sec-head">
          <span className="eyebrow">Alt-i-én</span>
          <h2>Fra kommentar til afhentning – uden manuelt arbejde</h2>
          <p>
            Når en kunde skriver "ja tak" på jeres Facebook-opslag, opretter robotten straks en reservation, svarer
            offentligt, synes godt om kommentaren og sender kunden en privat afhentningskode i Messenger. I styrer pluk
            og udlevering i JaTak Pro – alt sammen på autopilot.
          </p>
        </div>
      </section>

      <section className="features">
        <div>
          <div className="feature-ico">
            <MessageSquareReply size={22} />
          </div>
          <h2>Svarer kunderne automatisk</h2>
          <p>
            Robotten læser "ja tak"-kommentarer, svarer hver kunde offentligt med det samme og synes godt om
            kommentaren – på under ét sekund, døgnet rundt.
          </p>
        </div>
        <div>
          <div className="feature-ico">
            <MessageCircle size={22} />
          </div>
          <h2>Afhentningskode i Messenger</h2>
          <p>
            Hver kunde får en personlig, privat besked med deres unikke afhentningskode og ordredetaljer – så I kan
            tjekke ved kassen, at det er den rigtige, der henter.
          </p>
        </div>
        <div>
          <div className="feature-ico">
            <Boxes size={22} />
          </div>
          <h2>Lageret styrer sig selv</h2>
          <p>
            Max pr. kunde, automatisk venteliste og opslaget skifter selv til "udsolgt" – og tilbage, hvis nogen melder
            fra. "Ja tak" to gange bliver aldrig til to ordrer.
          </p>
        </div>
        <div>
          <div className="feature-ico">
            <CalendarClock size={22} />
          </div>
          <h2>Færre der ikke henter</h2>
          <p>
            Kunden får en personlig side med afhentningskode, kalender og SMS-påmindelse – og en knap til at melde
            afbud, så varen går videre. Vil I, kan kunder, der ikke hentede, skulle bekræfte næste gang.
          </p>
        </div>
        <div>
          <div className="feature-ico">
            <ScanLine size={22} />
          </div>
          <h2>Overblik ved kassen</h2>
          <p>
            Udleveringsskærm til tablet: søg på kode eller navn og tryk "udleveret". Hele dagens plukliste er klar, så
            I pakker rigtigt første gang.
          </p>
        </div>
        <div>
          <div className="feature-ico">
            <BarChart3 size={22} />
          </div>
          <h2>Statistik og indsigt</h2>
          <p>
            Se omsætning, antal solgte varer og hvornår jeres kunder er mest aktive – så I rammer de bedste tidspunkter
            for jeres næste ja tak-tilbud.
          </p>
        </div>
        <div className="feature-wide" data-testid="landing-feature-paamindelse">
          <div className="feature-ico">
            <BellRing size={22} />
          </div>
          <div>
            <h2>Påmindelse i kommentaren</h2>
            <p>
              <span className="lp-stat">Op til 30 %</span>af den uafhentede ja tak-omsætning hjem: på sidste afhentningsdag minder botten kunden om
              at hente – direkte i deres egen kommentar.
            </p>
          </div>
          <Link to="/funktioner#paamindelse" data-testid="landing-paamindelse-link">Læs mere →</Link>
        </div>
      </section>
      <p className="ft-more-link"><Link to="/funktioner" data-testid="landing-features-more">Se alle funktioner →</Link></p>

      <section className="steps" id="komgodtigang">
        <div className="sec-head">
          <span className="eyebrow">Kom godt i gang</span>
          <h2>I er klar på tre trin</h2>
        </div>
        <ol>
          <li>
            <b>Opret jer med mail</b>
            <span>Prøv alt af i testtilstand med det samme – helt uden Facebook.</span>
          </li>
          <li>
            <b>Forbind jeres Facebook-side</b>
            <span>Ét klik med jeres egen Facebook-login.</span>
          </li>
          <li>
            <b>Slå første tilbud op</b>
            <span>Billede, pris, antal og afhentningstid. Robotten klarer resten.</span>
          </li>
        </ol>
      </section>

      <Testimonials limit={2} testid="landing-testimonials" />

      <section className="pricing" id="pris">
        <div className="price-card">
          <span className="price-badge">Alt inkluderet</span>
          <h2>Én pris. Alt med.</h2>
          <p className="price">
            <span className="tag big" data-testid="landing-price">{dkk(pricing.price_monthly_dkk)} kr.</span> <span className="muted">pr. måned ekskl. moms</span>
          </p>
          <ul>
            <li>
              <Check size={18} /> Ubegrænset antal tilbud og ordrer
            </li>
            <li>
              <Check size={18} /> Ingen gebyr pr. salg
            </li>
            <li>
              <Check size={18} /> SMS-påmindelser til jeres kunder
            </li>
            <li>
              <Check size={18} /> Opsig når som helst – ingen binding
            </li>
          </ul>
          <Link className="btn jatak big" to="/signup">
            Start {trial} dages gratis prøve
          </Link>
          <Link className="pr-more" to="/priser" data-testid="landing-pricing-more">Se alt, der er med →</Link>
        </div>
      </section>

      <section className="faq" id="faq">
        <div className="sec-head">
          <span className="eyebrow">FAQ</span>
          <h2>Har du spørgsmål?</h2>
        </div>
        <div className="faq-grid" data-testid="faq-grid">
          {faqsFor(pricing).map((f) => (
            <FaqItem key={f.q} q={f.q} a={f.a} />
          ))}
        </div>
      </section>

      <section className="cta-band" data-testid="cta-band">
        <span className="eyebrow">Klar til flere solgte varer?</span>
        <h2>Slå jeres første ja tak-tilbud op i dag</h2>
        <p>Opret jer på under et minut og prøv hele systemet i testtilstand – uden at forbinde Facebook.</p>
        <Link className="btn jatak big" to="/signup" data-testid="cta-signup-btn">
          Prøv gratis i {trial} dage
        </Link>
      </section>
    </SiteShell>
  );
}
